/* eslint-disable @typescript-eslint/no-explicit-any */

// By default we redact all fields
const PII_REGEX = /.*/i;

/** Matches ids such as document.id or object.applicationId */
const ALLOW_REGEX = /\.(id|.+Id)$/;
type Redacted<T extends Record<string, any>> = {
  [K in keyof T]: T[K] extends Record<string, any> ? Redacted<T[K]> : T[K] | string;
};

/**
 * Recursively redacts PII from an object
 * @param obj The object to redact
 * @param fieldNameRegex Any field that matches this regex will be redacted unless it also matches ALLOW_REGEX, the field name is the full field path
 * @param redactWith The value to substitute redacted fields
 */
export function redactObject<T extends Record<string, any>>(
  obj: T,
  fieldNameRegex: RegExp = PII_REGEX,
  allowRegex: RegExp = ALLOW_REGEX,
  redactWith = '[REDACTED]',
): Redacted<T> {
  return redactObjectInternal('', obj, fieldNameRegex, allowRegex, redactWith);
}

function redactObjectInternal<T>(
  fieldName: string,
  obj: T,
  fieldNameRegex: RegExp = PII_REGEX,
  allowRegex: RegExp = ALLOW_REGEX,
  redactWith = '[REDACTED]',
): T {
  // We don't consider falsy values to be PII
  if (!obj) {
    return obj;
  }

  // true and false are not PII
  if (typeof obj === 'boolean') {
    return obj;
  }

  // Handle array case
  if (Array.isArray(obj)) {
    return obj.map((item, index) =>
      redactObjectInternal(`${fieldName}[${index}]`, item, fieldNameRegex, allowRegex, redactWith),
    ) as T;
  }

  // Handle primitive case
  if (typeof obj !== 'object') {
    if (fieldNameRegex.test(fieldName) && !allowRegex.test(fieldName)) {
      return redactWith as T;
    }
    return obj;
  }

  // Handle object case
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      redactObjectInternal(`${fieldName}.${key}`, value, fieldNameRegex, allowRegex, redactWith),
    ]),
  ) as T;
}
